import Link from "next/link";
import { Building2, ShieldCheck } from "lucide-react";
import { companyBrandFont } from "@/lib/jobs/fonts";
import { cn } from "@/lib/utils";

export interface CompanyWordmarkProps {
  companyName: string;
  verified?: boolean;
  href?: string;
  variant?: "card" | "cardCompact" | "hero" | "sidebar";
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export function CompanyWordmark({
  companyName,
  verified = false,
  href,
  variant = "card",
  onClick,
}: CompanyWordmarkProps) {
  const isHero = variant === "hero";
  const isCompact = variant === "cardCompact";
  const isSidebar = variant === "sidebar";

  const symbolBoxClass = cn(
    "inline-flex shrink-0 items-center justify-center rounded-full border transition-colors",
    isHero
      ? "h-8.5 w-8.5 border-white/20 bg-white/[.09] text-[#cbd5e1] group-hover/company:border-white/35 group-hover/company:bg-white/[.14] group-hover/company:text-white sm:h-9.5 sm:w-9.5"
      : isCompact
        ? "h-7.5 w-7.5 border-[#d1d5db] bg-gradient-to-b from-[#f9fafb] to-[#eef0f3] text-[#475569] shadow-[0_1px_2px_rgba(15,23,42,0.05)] group-hover/company:border-[#9ca3af] group-hover/company:text-[#1f2937]"
        : "h-8 w-8 border-[#d1d5db] bg-gradient-to-b from-[#f9fafb] to-[#eef0f3] text-[#475569] shadow-[0_1px_2px_rgba(15,23,42,0.05)] group-hover/company:border-[#9ca3af] group-hover/company:text-[#1f2937] sm:h-8.5 sm:w-8.5",
  );

  const nameClass = cn(
    companyBrandFont.className,
    "font-bold leading-tight tracking-[-0.03em] transition-colors",
    isHero
      ? "text-[23px] text-[#cbd5e1] group-hover/company:text-white sm:text-[26px] lg:text-[28px]"
      : isSidebar
        ? "line-clamp-2 text-[20px] text-[#4b5563] group-hover/company:text-[#1f2937] sm:text-[21px]"
        : isCompact
          ? "text-[18px] text-[#4b5563] group-hover/company:text-[#1f2937] sm:text-[19.5px]"
          : "text-[20px] text-[#4b5563] group-hover/company:text-[#1f2937] sm:text-[22px]",
  );

  const verifiedBadgeClass = cn(
    "inline-flex shrink-0 items-center justify-center rounded-full border transition-colors",
    isHero
      ? "h-6 w-6 border-white/20 bg-white/[.09] text-[#cbd5e1] group-hover/company:border-white/35 group-hover/company:text-white sm:h-6.5 sm:w-6.5"
      : "h-5.5 w-5.5 border-[#d1d5db] bg-[#f3f4f6] text-[#475569] group-hover/company:border-[#9ca3af] group-hover/company:text-[#1f2937]",
  );

  const symbolIconSize = isHero ? 17 : isCompact ? 14 : 15;
  const shieldIconSize = isHero ? 14 : 12.5;

  const content = (
    <>
      <span className={symbolBoxClass} aria-hidden="true">
        <Building2 size={symbolIconSize} strokeWidth={2.1} />
      </span>
      <span className={nameClass}>{companyName}</span>
      {verified && (
        <span title="Verified Employer" className={verifiedBadgeClass}>
          <ShieldCheck
            size={shieldIconSize}
            strokeWidth={2.3}
            aria-label="Verified employer"
          />
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <Link
        prefetch={false}
        href={href}
        onClick={onClick}
        className="group/company inline-flex max-w-full items-center gap-2.5 rounded-[8px] py-0.5 pr-1 transition"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="inline-flex max-w-full items-center gap-2.5 py-0.5">
      {content}
    </div>
  );
}
